import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { IncidentListResponse, IncidentRow } from "entities/incidents";
import { IncidentStatistics } from "entities/common";

import { SilentMeta } from "store/silent";

type IncidentsState = {
  statistics: IncidentStatistics;
  isStatisticsLoading: boolean;
  list: IncidentRow[];
  meta: { total: number; page: number; page_size: number };
  totalIncidents: number;
  hasIncidents: boolean;
  isListLoading: boolean;
  listLoaded: boolean;
};

const initialState: IncidentsState = {
  statistics: { total_active: 0, active_high: 0, resolved_last_30d: 0 },
  isStatisticsLoading: false,
  list: [],
  meta: { total: 0, page: 1, page_size: 20 },
  totalIncidents: 0,
  hasIncidents: false,
  isListLoading: false,
  listLoaded: false,
};

const incidentsSlice = createSlice({
  name: "incidents",
  initialState,
  reducers: {
    getIncidentStatisticsRequest: {
      reducer(state, action: PayloadAction<string | undefined, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isStatisticsLoading = true;
      },
      prepare: (query?: string, meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getIncidentStatisticsSuccess(state, action: PayloadAction<IncidentStatistics>) {
      state.statistics = action.payload;
      state.isStatisticsLoading = false;
    },
    getIncidentStatisticsFailure(state) {
      state.isStatisticsLoading = false;
    },
    resetIncidentStatistics(state) {
      state.statistics = initialState.statistics;
      state.isStatisticsLoading = false;
    },
    getIncidentListRequest: {
      reducer(state, action: PayloadAction<string, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isListLoading = true;
      },
      prepare: (query: string, meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getIncidentListSuccess(state, action: PayloadAction<IncidentListResponse>) {
      state.list = action.payload.items;
      state.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
      };
      state.totalIncidents = action.payload.total_incidents;
      state.hasIncidents = action.payload.has_incidents;
      state.isListLoading = false;
      state.listLoaded = true;
    },
    getIncidentListFailure(state) {
      state.isListLoading = false;
      state.listLoaded = true;
    },
    resetIncidentList(state) {
      state.list = [];
      state.meta = initialState.meta;
      state.totalIncidents = 0;
      state.hasIncidents = false;
      state.isListLoading = false;
      state.listLoaded = false;
    },
  },
});

export const {
  getIncidentStatisticsRequest,
  getIncidentStatisticsSuccess,
  getIncidentStatisticsFailure,
  resetIncidentStatistics,
  getIncidentListRequest,
  getIncidentListSuccess,
  getIncidentListFailure,
  resetIncidentList,
} = incidentsSlice.actions;

export default incidentsSlice.reducer;
